import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FiPlus,
  FiFileText,
  FiDownload,
  FiTrash2,
  FiRefreshCw,
  FiUploadCloud,
  FiRotateCcw,
  FiMail,
} from 'react-icons/fi';
import api, { downloadAuthenticatedFile } from '../../config/api';
import { queryKeys, staleTimes } from '../../config/queryKeys';
import MoreMenu from '../MoreMenu';
import { formatDateOnly } from '../../utils/formatTimestamp';
import { appAlert, appConfirm } from '../../utils/appDialog';
import { GlPostingStatus, MoneyText } from '../commercial/CommercialUi';
import {
  EmptyState as ErpEmpty,
  ListState as ErpListState,
  StatusBadge,
} from '../shared/Ui';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';

function docTypeLabel(type) {
  if (type === 'invoice') return 'Invoice';
  if (type === 'quotation') return 'Quotation';
  return type || '—';
}

function salesDocBusy(mutation, id) {
  return Boolean(mutation.isLoading && mutation.variables === id);
}

function salesDocMenuItems({
  d,
  canManage,
  canPost,
  canUnpost,
  postGlMutation,
  unpostGlMutation,
  generateMutation,
  sendEmailMutation,
  deleteDocMutation,
}) {
  const posting = salesDocBusy(postGlMutation, d.id);
  const unposting = salesDocBusy(unpostGlMutation, d.id);
  const generating = salesDocBusy(generateMutation, d.id);
  const sending = salesDocBusy(sendEmailMutation, d.id);
  const canEmail = d.doc_type === 'quotation' || d.doc_type === 'invoice';
  return [
    {
      id: 'post',
      label: posting ? 'Posting…' : 'Post to GL',
      icon: FiUploadCloud,
      hidden: !canPost,
      disabled: posting,
      onSelect: () => postGlMutation.mutate(d.id),
    },
    {
      id: 'unpost',
      label: unposting ? 'Reverting…' : 'Revert to draft',
      icon: FiRotateCcw,
      hidden: !canUnpost,
      disabled: unposting,
      onSelect: async () => {
        const ok = await appConfirm(
          `Revert ${d.number} from the GL? You can correct it and post again.`
        );
        if (ok) unpostGlMutation.mutate(d.id);
      },
    },
    {
      id: 'pdf',
      label: generating ? 'Generating PDF…' : 'Generate PDF',
      icon: FiRefreshCw,
      hidden: !canManage,
      disabled: generating,
      onSelect: () => generateMutation.mutate(d.id),
    },
    {
      id: 'send-email',
      label: sending ? 'Sending email…' : 'Send email',
      icon: FiMail,
      hidden: !canManage || !canEmail,
      disabled: sending,
      onSelect: async () => {
        const to = String(d.client_email || '').trim();
        if (!to) {
          void appAlert('Add a client email on this quotation or invoice before sending.');
          return;
        }
        const ok = await appConfirm(
          `Send ${d.number} to ${to}? The PDF and the email body from Settings will be included.`
        );
        if (ok) sendEmailMutation.mutate(d.id);
      },
    },
    {
      id: 'download',
      label: 'Download PDF',
      icon: FiDownload,
      hidden: !d.pdf_r2_key,
      onSelect: async () => {
        try {
          await downloadAuthenticatedFile(
            `/services/sales/documents/${d.id}/pdf`,
            `${d.number}.pdf`
          );
        } catch (e) {
          void appAlert(e.response?.data?.error || e.message);
        }
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: FiTrash2,
      danger: true,
      hidden: !canManage,
      onSelect: async () => {
        if (await appConfirm(`Delete ${d.number}?`)) {
          deleteDocMutation.mutate(d.id);
        }
      },
    },
  ];
}

/**
 * Quotes & invoices list — ERP panel pattern (same kit as Buying / other Sales tabs).
 */
const ServicesSalesPanel = ({
  canManage,
  canPostGl = false,
  focusDocumentId = null,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState('');

  const docsQuery = useQuery(
    queryKeys.salesDocuments(filterType),
    () =>
      api
        .get('/services/sales/documents', {
          params: filterType ? { doc_type: filterType } : undefined,
        })
        .then((r) => r.data),
    { staleTime: staleTimes.lists }
  );

  const documents = docsQuery.data || [];
  const {
    pageItems: pagedDocuments,
    pagination: docsPagination,
    setPage: setDocsPage,
  } = useClientPager(documents, { resetKey: filterType || '' });

  const invalidate = () => {
    queryClient.invalidateQueries(queryKeys.salesDocuments());
    queryClient.invalidateQueries(['service-sales-templates']);
  };

  const deleteDocMutation = useMutation((id) => api.delete(`/services/sales/documents/${id}`), {
    onSuccess: invalidate,
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const sendEmailMutation = useMutation(
    (id) => api.post(`/services/sales/documents/${id}/send-email`),
    {
      onSuccess: (res) => {
        invalidate();
        const to = res.data?.to;
        void appAlert(to ? `Email sent to ${to}` : 'Email sent');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const generateMutation = useMutation(
    (id) => api.post(`/services/sales/documents/${id}/generate-pdf`),
    {
      onSuccess: async (res) => {
        invalidate();
        const id = res.data?.document?.id;
        if (id) {
          try {
            await downloadAuthenticatedFile(
              `/services/sales/documents/${id}/pdf`,
              `${res.data.document.number || 'document'}.pdf`
            );
          } catch (e) {
            void appAlert(e.response?.data?.error || 'PDF generated but download failed');
          }
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const postGlMutation = useMutation(
    (id) => api.post(`/accounting/sales-documents/${id}/post-to-gl`),
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries(['erp-report']);
        queryClient.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const unpostGlMutation = useMutation(
    (id) => api.post(`/accounting/sales-documents/${id}/unpost-from-gl`),
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries(['erp-report']);
        queryClient.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  useEffect(() => {
    if (!focusDocumentId) return;
    navigate(`/sales/documents/${focusDocumentId}`, { replace: true });
  }, [focusDocumentId, navigate]);

  return (
    <section className="erp-panel">
      <div className="erp-panel-head">
        <div>
          <h2>Quotes &amp; invoices</h2>
          <p className="erp-panel-meta">
            {documents.length} {documents.length === 1 ? 'document' : 'documents'}
          </p>
        </div>
        <div className="erp-panel-actions">
          <label className="erp-inline-filter">
            <span className="visually-hidden">Filter by type</span>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              aria-label="Filter by document type"
            >
              <option value="">All types</option>
              <option value="quotation">Quotations</option>
              <option value="invoice">Invoices</option>
            </select>
          </label>
          {canManage && (
            <>
              <Link
                className="btn btn-secondary btn-sm"
                to="/sales/documents/new?type=quotation"
              >
                <FiFileText aria-hidden /> New quotation
              </Link>
              <Link className="btn btn-primary btn-sm" to="/sales/documents/new?type=invoice">
                <FiPlus aria-hidden /> New invoice
              </Link>
            </>
          )}
        </div>
      </div>

      <ErpListState
        query={docsQuery}
        isEmpty={documents.length === 0}
        empty={
          <ErpEmpty
            icon={FiFileText}
            title="No quotes or invoices yet"
            hint="Create a quotation or invoice for a customer."
          />
        }
      >
        <div className="erp-table-scroll">
          <table className="erp-table">
            <thead>
              <tr>
                <th scope="col">Number</th>
                <th scope="col">Type</th>
                <th scope="col">Client</th>
                <th scope="col">Status</th>
                <th scope="col">GL / cash</th>
                <th scope="col">Currency</th>
                <th scope="col" className="erp-num">
                  Total
                </th>
                <th scope="col">Issued</th>
                <th scope="col" className="erp-cell-actions">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedDocuments.map((d) => {
                const canPost =
                  canPostGl &&
                  d.doc_type === 'invoice' &&
                  d.gl_status !== 'posted' &&
                  !['draft', 'void', 'rejected'].includes(d.status);
                const canUnpost =
                  canPostGl &&
                  d.doc_type === 'invoice' &&
                  d.gl_status === 'posted' &&
                  d.cash_status !== 'paid' &&
                  d.cash_status !== 'partial';
                return (
                  <tr key={d.id}>
                    <td className="erp-cell-primary">
                      <Link to={`/sales/documents/${d.id}`}>
                        <span className="erp-code">{d.number}</span>
                      </Link>
                    </td>
                    <td>{docTypeLabel(d.doc_type)}</td>
                    <td>{d.client_name || '—'}</td>
                    <td>
                      <StatusBadge status={d.status} />
                    </td>
                    <td>
                      <GlPostingStatus
                        status={d.gl_status}
                        journalEntryNumber={d.journal_entry_number}
                        cashStatus={d.cash_status}
                      />
                    </td>
                    <td>
                      <span className="erp-code">{d.currency || 'USD'}</span>
                    </td>
                    <td className="erp-num">
                      <MoneyText amount={d.total} currency={d.currency} unit="dollars" />
                    </td>
                    <td>{d.issued_at ? formatDateOnly(d.issued_at) : '—'}</td>
                    <td className="erp-cell-actions">
                      <MoreMenu
                        label={`Actions for ${d.number}`}
                        size="sm"
                        items={salesDocMenuItems({
                          d,
                          canManage,
                          canPost,
                          canUnpost,
                          postGlMutation,
                          unpostGlMutation,
                          generateMutation,
                          sendEmailMutation,
                          deleteDocMutation,
                        })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <ListPager
          page={docsPagination.page}
          pageCount={docsPagination.pageCount}
          total={docsPagination.total}
          limit={docsPagination.limit}
          onPageChange={setDocsPage}
        />
      </ErpListState>
    </section>
  );
};

export default ServicesSalesPanel;
